import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePicker } from '@/components/DatePicker'

type AccLine = { account_id: number; code: string; name: string; balance: string }

type BS = {
  as_of: string
  assets: AccLine[]
  total_assets: string
  liabilities: AccLine[]
  total_liabilities: string
  equity: AccLine[]
  current_year_earnings: string
  total_equity: string
  total_liabilities_and_equity: string
  is_balanced: boolean
  difference: string
}

function today() { return new Date().toISOString().slice(0, 10) }

export default function ReportBalanceSheet() {
  const [asOf, setAsOf] = useState(today())

  const { data, isLoading } = useQuery({
    queryKey: ['report', 'bs', asOf],
    queryFn: async () => {
      return (await api.get<BS>('/finance/reports/balance-sheet/', { params: { as_of: asOf } })).data
    },
  })

  return (
    <AdminLayout>
      <h2 className="text-2xl font-bold text-gray-800 mb-1">Balance Sheet</h2>
      <p className="text-sm text-gray-500 mb-6">
        Assets = Liabilities + Equity. A snapshot of the company's financial position on a given date.
      </p>

      <div className="flex items-end space-x-3 mb-4 bg-white p-3 rounded-lg shadow">
        <Field>
          <FieldLabel htmlFor="bs-as-of">As of</FieldLabel>
          <DatePicker id="bs-as-of" value={asOf} onChange={(v) => setAsOf(v)} />
        </Field>
        {data && (
          <div className="ml-auto text-sm">
            <span className={`inline-flex items-center gap-1 ${data.is_balanced ? 'text-green-700' : 'text-red-700'}`}>
              {data.is_balanced
                ? (<><Check className="h-4 w-4" /> Balanced</>)
                : (<><X className="h-4 w-4" /> Off by {Number(data.difference).toFixed(2)}</>)}
            </span>
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : !data ? null : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
          {/* Assets */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 border-b pb-1">
              Assets
            </h3>
            <RowList rows={data.assets} />
            <TotalRow label="Total Assets" value={data.total_assets} />
          </div>

          {/* Liabilities + Equity */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 border-b pb-1">
              Liabilities
            </h3>
            <RowList rows={data.liabilities} />
            <TotalRow label="Total Liabilities" value={data.total_liabilities} sub />

            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 mt-5 border-b pb-1">
              Equity
            </h3>
            <RowList rows={data.equity} />
            <div className="flex items-center justify-between py-1 pl-3">
              <span className="text-sm text-gray-700">
                <span className="font-mono text-xs text-gray-500 mr-3">—</span>
                Current Year Earnings
              </span>
              <span className="font-mono text-sm">{Number(data.current_year_earnings).toFixed(2)}</span>
            </div>
            <TotalRow label="Total Equity" value={data.total_equity} sub />

            <div className="mt-4 pt-2 border-t-2 border-gray-300">
              <TotalRow label="Total Liabilities & Equity" value={data.total_liabilities_and_equity} highlight />
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

function RowList({ rows }: { rows: AccLine[] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-gray-400 italic py-1 pl-3">— no balances —</p>
  }
  return (
    <table className="min-w-full text-sm mb-2">
      <tbody>
        {rows.map((r) => (
          <tr key={r.account_id}>
            <td className="pl-3 py-1 font-mono text-xs text-gray-500 w-20">{r.code}</td>
            <td className="py-1">{r.name}</td>
            <td className="py-1 text-right font-mono">{Number(r.balance).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function TotalRow({ label, value, sub, highlight }: { label: string; value: string; sub?: boolean; highlight?: boolean }) {
  return (
    <div className={`flex items-center justify-between py-2 ${sub ? 'border-t border-gray-200' : ''} ${highlight ? 'font-bold' : 'font-semibold'}`}>
      <span className={highlight ? 'text-gray-900' : 'text-gray-700'}>{label}</span>
      <span className="font-mono">{Number(value).toFixed(2)}</span>
    </div>
  )
}

